import { useState, useEffect, useCallback } from 'react';
import type {
  Program,
  ProgramTargetSkill,
  StudentProgramSkill,
  StudentProgramCompletionWithProgramName,
  ProgramCertificateMetadata,
  AcademyCertificateBranding,
} from '@court-sync/shared';
import { areAllProgramTargetSkillsCompleted } from '@court-sync/shared';
import { programService } from '@/services/program';
import { academyService } from '@/services/academy';
import { CertificateDownloadButton } from '@/components/certificates/CertificateDownloadButton';
import { coachNotesService } from '@/services/coachNotesService';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Loader2, BookOpen, CheckCircle2, Lock, Award } from 'lucide-react';
import toast from 'react-hot-toast';

type ProgramBundle = {
  program: Program;
  skills: StudentProgramSkill[];
};

export interface StudentProgramSkillsPanelProps {
  academyId: string;
  studentId: string;
  programIds: string[];
  /** Increment to refetch skill rows (e.g. after pathway note with “achieved”) */
  refreshKey?: number;
  /** After a pathway note is created from this panel, refresh notes + assessment history on the parent page */
  onPathwayAssessmentRecorded?: () => void | Promise<void>;
  /** For PDF certificate */
  academyName?: string;
  studentFirstName?: string;
  studentLastName?: string;
  academyLogoUrl?: string;
  academyPrimaryColor?: string;
}

export function StudentProgramSkillsPanel({
  academyId,
  studentId,
  programIds,
  refreshKey = 0,
  onPathwayAssessmentRecorded,
  academyName = 'Academy',
  studentFirstName = 'Student',
  studentLastName = '',
  academyLogoUrl,
  academyPrimaryColor,
}: StudentProgramSkillsPanelProps) {
  const [bundles, setBundles] = useState<ProgramBundle[]>([]);
  const [completions, setCompletions] = useState<StudentProgramCompletionWithProgramName[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [selectedSkillName, setSelectedSkillName] = useState<string>('');
  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [manualCompleteLoadingId, setManualCompleteLoadingId] = useState<string | null>(null);
  const [certificateBranding, setCertificateBranding] = useState<AcademyCertificateBranding | null>(null);

  useEffect(() => {
    let cancelled = false;
    academyService
      .getCertificateBranding(academyId)
      .then((b) => {
        if (!cancelled) setCertificateBranding(b);
      })
      .catch(() => {
        if (!cancelled) setCertificateBranding(null);
      });
    return () => {
      cancelled = true;
    };
  }, [academyId]);

  const loadCompletions = useCallback(async (): Promise<StudentProgramCompletionWithProgramName[]> => {
    try {
      const rows = await programService.getStudentProgramCompletions(studentId, academyId);
      setCompletions(rows);
      return rows;
    } catch {
      setCompletions([]);
      return [];
    }
  }, [studentId, academyId]);

  const load = useCallback(async () => {
    if (programIds.length === 0) {
      setBundles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const allPrograms = await programService.getPrograms(academyId, true);
      const relevant = allPrograms.filter((p) => programIds.includes(p.id));
      const withSkills = await Promise.all(
        relevant.map(async (program) => {
          const skills = await programService.getStudentProgramSkills(
            program.id,
            studentId,
            academyId
          );
          return { program, skills };
        })
      );
      setBundles(withSkills);
      await loadCompletions();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to load program skills');
      setBundles([]);
    } finally {
      setLoading(false);
    }
  }, [academyId, studentId, programIds, loadCompletions]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const openAchievedModal = (programId: string, skillName: string) => {
    setSelectedProgramId(programId);
    setSelectedSkillName(skillName);
    setRating(5);
    setComment('');
    setIsPrivate(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedProgramId(null);
    setSelectedSkillName('');
    setSubmitting(false);
  };

  const handleSubmitAchieved = async () => {
    if (!selectedProgramId || !selectedSkillName.trim()) return;
    setSubmitting(true);
    try {
      await coachNotesService.createNote(studentId, {
        academyId,
        content: comment.trim(),
        category: 'progress',
        isPrivate,
        programId: selectedProgramId,
        programSkillName: selectedSkillName.trim(),
        rating: rating as 1 | 2 | 3 | 4 | 5,
        skillAchieved: true,
      });
      toast.success('Pathway skill recorded');
      closeModal();
      await load();
      await loadCompletions();
      await onPathwayAssessmentRecorded?.();
    } catch (e) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast.error(msg || (e instanceof Error ? e.message : 'Could not save'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
        Loading program skills…
      </div>
    );
  }

  if (bundles.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No program pathways linked to this student&apos;s lessons yet. Programs are attached when
          lessons are created from a pathway.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {bundles.map(({ program, skills }) => (
        <Card key={program.id}>
          <CardHeader className="pb-2">
            <div className="flex items-start gap-2">
              <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
              <div>
                <h3 className="text-lg font-semibold text-foreground">{program.name}</h3>
                {program.description ? (
                  <p className="mt-1 text-sm text-muted-foreground">{program.description}</p>
                ) : null}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {program.targetSkills.length === 0 ? (
              <p className="text-sm text-muted-foreground">No target skills defined for this pathway.</p>
            ) : (
              program.targetSkills.map((target: ProgramTargetSkill) => {
                const row = skills.find((s) => s.skillName === target.name);
                const completed = row?.completed ?? false;
                return (
                  <div
                    key={target.name}
                    className="flex flex-col gap-3 rounded-md border border-border/80 bg-muted/30 p-3 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      {completed ? (
                        <div className="flex items-start gap-2">
                          <CheckCircle2
                            className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
                            aria-hidden
                          />
                          <div>
                            <span className="font-medium text-foreground">{target.name}</span>
                            <span className="ml-2 text-sm font-normal text-emerald-700 dark:text-emerald-400">
                              Achieved
                            </span>
                            {target.description ? (
                              <span className="mt-0.5 block text-sm text-muted-foreground">
                                {target.description}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="font-medium text-foreground">{target.name}</span>
                          {target.description ? (
                            <span className="mt-0.5 block text-sm text-muted-foreground">
                              {target.description}
                            </span>
                          ) : null}
                        </>
                      )}
                    </div>
                    {!completed ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className="shrink-0 self-start"
                        onClick={() => openAchievedModal(program.id, target.name)}
                      >
                        Mark as achieved
                      </Button>
                    ) : null}
                  </div>
                );
              })
            )}
          </CardContent>
          {(() => {
            const completion = completions.find((c) => c.programId === program.id);
            const skillsComplete = areAllProgramTargetSkillsCompleted(program, skills);
            const pathwayComplete = !!completion || skillsComplete;
            const showManualComplete = !completion && program.targetSkills.length === 0;

            const handleMarkManual = async () => {
              setManualCompleteLoadingId(program.id);
              try {
                await programService.markProgramComplete(program.id, studentId, academyId);
                toast.success('Pathway marked complete');
                await load();
                await loadCompletions();
              } catch (e) {
                const msg =
                  e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
                    : null;
                toast.error(msg || (e instanceof Error ? e.message : 'Could not mark complete'));
              } finally {
                setManualCompleteLoadingId(null);
              }
            };

            if (!pathwayComplete && !showManualComplete) return null;

            return (
              <div className="border-t border-border/80 bg-muted/20 px-6 py-4 space-y-3">
                {pathwayComplete ? (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300">
                      <Award className="h-5 w-5 shrink-0" aria-hidden />
                      <span className="font-medium">Pathway complete</span>
                      {completion?.certificateIssued ? (
                        <span className="text-sm font-normal text-muted-foreground">
                          · Certificate on file
                        </span>
                      ) : null}
                    </div>
                    <CertificateDownloadButton
                      loadCertificate={async () => {
                        let row = completions.find((c) => c.programId === program.id);
                        if (!row && skillsComplete) {
                          const rows = await loadCompletions();
                          row = rows.find((c) => c.programId === program.id);
                        }
                        if (!row && skillsComplete) {
                          await programService.markProgramComplete(program.id, studentId, academyId);
                          const rows = await loadCompletions();
                          row = rows.find((c) => c.programId === program.id);
                        }
                        if (!row) {
                          throw new Error('Complete the pathway first');
                        }
                        let updated: StudentProgramCompletionWithProgramName;
                        if (!row.certificateIssued) {
                          const issued = await programService.markCertificateIssued(
                            program.id,
                            studentId,
                            academyId
                          );
                          updated = { ...issued, programName: row.programName ?? program.name };
                        } else {
                          updated = row;
                        }
                        const branding =
                          certificateBranding ?? (await academyService.getCertificateBranding(academyId));
                        const meta: ProgramCertificateMetadata = {
                          studentFirstName,
                          studentLastName,
                          programName: program.name,
                          academyName,
                          completedAt: new Date(updated.completedAt).toISOString(),
                          academyLogoUrl: branding.logoUrl ?? academyLogoUrl,
                          academyPrimaryColor: branding.primaryColor ?? academyPrimaryColor,
                          certificateSignerName: branding.signerName,
                          certificateSignerTitle: branding.signerTitle,
                          certificateSignatureImageUrl: branding.signatureImageUrl,
                        };
                        return {
                          meta,
                          filenameBase: `certificate-${program.name}`,
                        };
                      }}
                      onAfterDownload={() => void loadCompletions()}
                      onError={(e) => {
                        const msg =
                          e && typeof e === 'object' && 'response' in e
                            ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
                            : null;
                        toast.error(
                          msg || (e instanceof Error ? e.message : 'Could not generate certificate')
                        );
                      }}
                      size="sm"
                      variant="default"
                    >
                      Download certificate (PDF)
                    </CertificateDownloadButton>
                  </div>
                ) : null}
                {showManualComplete ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                      No target skills are defined for this program. Mark the pathway complete manually when
                      appropriate.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={manualCompleteLoadingId === program.id}
                      onClick={() => void handleMarkManual()}
                    >
                      {manualCompleteLoadingId === program.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                          Saving…
                        </>
                      ) : (
                        'Mark pathway complete'
                      )}
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })()}
        </Card>
      ))}

      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-md" aria-describedby="pathway-achieved-desc">
          <DialogHeader>
            <DialogTitle>Mark skill as achieved</DialogTitle>
            <DialogDescription id="pathway-achieved-desc">
              Add an optional comment and rating. This creates the same pathway record as a Progress coach note and
              appears under Progress → Assessment history.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="pathway-skill-rating" className="mb-1.5 block">
                Rating (1–5)
              </Label>
              <select
                id="pathway-skill-rating"
                value={rating}
                onChange={(e) => setRating(Number(e.target.value) as 1 | 2 | 3 | 4 | 5)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="pathway-skill-comment" className="mb-1.5 block">
                Comment (optional)
              </Label>
              <Textarea
                id="pathway-skill-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="e.g. Great progress this term…"
                rows={4}
                className="resize-y min-h-[96px]"
              />
            </div>
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border/60 p-2">
              <Checkbox
                checked={isPrivate}
                onCheckedChange={(v) => setIsPrivate(v === true)}
                className="mt-0.5"
                id="pathway-note-private"
              />
              <span className="text-sm text-muted-foreground">
                <Lock className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden />
                Private (coaches only)
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSubmitAchieved()} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save & mark achieved'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
